import { tokens } from '@neo4j-ndl/base';
import { Button, Drawer, TextInput, Typography, useMediaQuery } from '@neo4j-ndl/react';
import { type IpFilter, type IpFilterFilteredEntities, type IpNetwork, useActiveOrg } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { PermissionTooltip } from '@nx/ui';
import { useContext, useEffect, useMemo, useState } from 'react';

import { AllowListSelector } from './allow-list-selector';
import { EntityTree } from './entity-tree';
import { formHasChanged } from './helpers';
import { IpFilteringScopeContext } from './ip-filter-scope';
import type { AllowListEntry, IpFilterData } from './types';
import { IP_FILTERING_ACTION, IP_FILTERING_SCOPE, MAX_ALLOW_LIST_SIZE } from './types';

interface IpFilterSidebarProps {
  onClose: () => void;
  action: IP_FILTERING_ACTION;
  onSubmit: (data: IpFilterData) => void;
  hasActionPermission?: boolean;
  existingIpFilter?: IpFilter;
}

export const IpFilterSidebar = ({
  onClose,
  action,
  hasActionPermission = false,
  onSubmit,
  existingIpFilter,
}: IpFilterSidebarProps) => {
  const { breakpoints } = tokens;

  const { scope } = useContext(IpFilteringScopeContext);

  const [data, setData] = useState<IpFilterData>({
    name: '',
    description: '',
    allowList: [],
    filteredEntities: {
      organizations: [],
      projects: [],
      instances: [],
    },
  });

  const [editable, setEditable] = useState<boolean>(true);
  const actionText = action === IP_FILTERING_ACTION.CREATE ? 'Create' : 'Confirm Changes';
  const activeOrg = useActiveOrg();

  useEffect(() => {
    if (action === IP_FILTERING_ACTION.UPDATE && existingIpFilter) {
      setEditable(false);
      setData({
        name: existingIpFilter.name,
        description: existingIpFilter.description,
        allowList: existingIpFilter.allowList.map((ip: IpNetwork) => {
          return {
            IpRange: `${ip.address}/${ip.prefixLen}`,
            Description: ip.description,
          };
        }),
        filteredEntities: existingIpFilter.filteredEntities,
      });
    }
  }, [action, existingIpFilter]);

  // Mobile view styling
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.sm})`);
  const defaultWidth = useMemo(() => {
    if (isMobile) {
      return window.innerWidth;
    }
    return 480;
  }, [isMobile]);

  const handleNameChange = (name: string) => {
    setData((prev: IpFilterData) => ({ ...prev, name }));
  };

  const handleDescriptionChange = (description: string) => {
    setData((prev: IpFilterData) => ({ ...prev, description }));
  };

  const handleEntityChange = (filteredEntities: IpFilterFilteredEntities) => {
    setData((prev: IpFilterData) => ({ ...prev, filteredEntities }));
  };

  const handleAllowListChange = (allowList: AllowListEntry[]) => {
    setData((prev: IpFilterData) => ({ ...prev, allowList }));
  };

  const missingRequiredFields = (): boolean => {
    if (isNullish(data.name) || data.name.trim() === '' || data.allowList?.length === 0) {
      return true;
    }
    return false;
  };

  const allowListTooLong = (): boolean => {
    if (data.allowList && data.allowList.length > MAX_ALLOW_LIST_SIZE[activeOrg.planType]) {
      return true;
    }
    return false;
  };

  return (
    <Drawer
      isExpanded
      onExpandedChange={onClose}
      position="right"
      className="!border-l"
      isResizeable
      resizeableProps={{
        defaultSize: { height: '100%', width: defaultWidth },
        minWidth: defaultWidth,
      }}
    >
      <Drawer.Header className="flex items-center gap-2">
        <Typography variant="h5">{action === IP_FILTERING_ACTION.CREATE ? 'Create' : 'Inspect'} IP Filter</Typography>
      </Drawer.Header>
      <Drawer.Body className="flex flex-col gap-5">
        <TextInput
          isFluid
          label="Name"
          value={data.name}
          isReadOnly={!editable}
          isRequired={true}
          onChange={(event) => handleNameChange(event.target.value)}
        />
        <TextInput
          isFluid
          label="Description"
          value={data.description}
          isReadOnly={!editable}
          isOptional={true}
          onChange={(event) => handleDescriptionChange(event.target.value)}
        />

        <EntityTree
          onChange={handleEntityChange}
          isReadOnly={!editable}
          filteredEntities={data.filteredEntities}
          existingIpFilterId={existingIpFilter?.id}
        />

        {/* All OR AllowList Radio  */}
        <AllowListSelector onChange={handleAllowListChange} isReadOnly={!editable} value={existingIpFilter} />
      </Drawer.Body>
      {scope === IP_FILTERING_SCOPE.ORGANIZATION && (
        <Drawer.Actions>
          {!editable ? (
            <PermissionTooltip hasButtonWrapper hasPermission={hasActionPermission}>
              <Button onClick={() => setEditable(true)} size="medium" isDisabled={!hasActionPermission}>
                Edit
              </Button>
            </PermissionTooltip>
          ) : (
            <>
              <Button onClick={() => onClose()} size="medium" fill="outlined" color="neutral">
                Cancel
              </Button>
              <PermissionTooltip hasButtonWrapper hasPermission={hasActionPermission}>
                <Button
                  onClick={() => onSubmit(data)}
                  size="medium"
                  isDisabled={
                    !hasActionPermission ||
                    missingRequiredFields() ||
                    allowListTooLong() ||
                    (existingIpFilter && !formHasChanged(data, existingIpFilter))
                  }
                >
                  {actionText}
                </Button>
              </PermissionTooltip>
            </>
          )}
        </Drawer.Actions>
      )}
    </Drawer>
  );
};
